import { FieldPath, useFormContext } from 'react-hook-form';
import { useAccountFormContext } from '../../context/useAccountFormContext';
import { FormFields } from '../../AccountForm.meta';
import { EXCLUDE_FROM_ASSET_BUCKETING_OPTION_VALUE, EXCLUDE_OPTION } from './AssetBucketing.meta';
import { AccountTaxStatusDTO, TaxStatusDTO } from '../../../../../../api/useAccountsQuery';
import { AccountType } from '../../../../../../api/useAccountsOptionsQuery';

interface Params {
  formKey: FieldPath<FormFields>;
  taxStatus: AccountTaxStatusDTO | null;
}

export const useAssetBucketing = ({ formKey, taxStatus }: Params) => {
  const { watch, setValue } = useFormContext<FormFields>();

  const [accountType, accountAssetBucket, taxBalances, orionManagementStyleId, positions] = watch([
    'bankAccount.AccountType',
    'bankAccount.CustomAssetBucketIndex',
    'bankAccount.Balances',
    'bankAccount.OrionManagementStyleId',
    'bankAccount.Positions',
    formKey,
  ]);

  const {
    options: {
      AvailableAssetBuckets: availableAssetBuckets,
      AllowAssetBucketingCustomization: allowAssetBucketingCustomization,
      ManagementStyles: managementStylesOptions,
      AssetBucketingByManagementStyle,
      DefaultAssetBucketingIndices,
      DefaultAssetBucketingIndicesByTaxStatus,
    },
  } = useAccountFormContext();

  const managementDefaultAssetBucketIndex = managementStylesOptions.find(
    option => option.id === orionManagementStyleId,
  )?.defaultBucket;

  const assetsOptions = availableAssetBuckets.map(({ Title: label }, index) => ({
    label,
    value: index,
  }));

  if (accountType === null) {
    throw new Error('Account type not found');
  }

  const options = [...assetsOptions, EXCLUDE_OPTION].map(base => {
    if (AssetBucketingByManagementStyle) {
      const isDefault =
        (managementDefaultAssetBucketIndex === null &&
          base.value === EXCLUDE_FROM_ASSET_BUCKETING_OPTION_VALUE) ||
        base.value === managementDefaultAssetBucketIndex;

      return {
        ...base,
        rightLabel: isDefault ? 'Default' : undefined,
      };
    }

    if (taxStatus === null) {
      return {
        ...base,
        rightLabel:
          base.value === DefaultAssetBucketingIndices[AccountType[accountType]]
            ? 'Default'
            : undefined,
      };
    }

    return {
      ...base,
      rightLabel:
        base.value ===
        DefaultAssetBucketingIndicesByTaxStatus[AccountType[accountType]]?.[
          TaxStatusDTO[taxStatus]
        ]
          ? 'Default'
          : undefined,
    };
  });

  const updateHoldingsAssetBuckets = (selectedAssetBucketIndex: number) => {
    if (!positions) {
      return;
    }

    positions.forEach((position, index) => {
      if (AssetBucketingByManagementStyle) {
        return;
      }

      const { TaxStatus: positionTaxStatus } = position;

      const assetBucket =
        taxStatus === null
          ? accountAssetBucket
          : taxBalances && taxBalances[TaxStatusDTO[taxStatus]]
          ? taxBalances[TaxStatusDTO[taxStatus]]?.AssetBucketIndex
          : null;

      const isAccountAssetBucketSameAsHoldingAssetBucket =
        position.AssetBucketIndex === assetBucket;

      const isAccountTaxSameAsHoldingsTax = taxStatus === positionTaxStatus;

      if (
        !isAccountTaxSameAsHoldingsTax ||
        (isAccountTaxSameAsHoldingsTax && !isAccountAssetBucketSameAsHoldingAssetBucket)
      ) {
        return;
      }

      setValue(`bankAccount.Positions.${index}.AssetBucketIndex`, selectedAssetBucketIndex);
    });
  };

  return {
    data: {
      allowAssetBucketingCustomization,
      options,
      orionManagementStyleId,
    },
    actions: {
      updateHoldingsAssetBuckets,
    },
  };
};
